import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { SKIP_ERROR_TOAST } from './error-toast.token';
import { shouldSkipSessionExpiryHandling } from './refresh.interceptor';
import { CommonService } from '../services/common.service';
import { GetterSetterService } from '../services/getter-setter.service';

/**
 * ErrorInterceptor — global handler for all HTTP / RestService failures.
 *
 * Flow: RestService.callServer → HttpClient → this interceptor → toast via
 * CommonService → error re-thrown so callers can reset local UI state only.
 *
 * Responsibilities:
 * - NS142 / forced session kill
 * - 401 session expiry (except auth challenge endpoints)
 * - Consistent user-facing messages (4xx / 5xx / network / validation)
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private readonly injector: Injector) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: unknown) => {
        const httpErr =
          err instanceof HttpErrorResponse
            ? err
            : new HttpErrorResponse({
                error: err,
                status: 0,
                statusText: 'Unknown Error',
                url: request.url,
              });

        this.handleError(request, httpErr);
        return throwError(() => httpErr);
      }),
    );
  }

  private handleError(request: HttpRequest<unknown>, err: HttpErrorResponse): void {
    const common = this.injector.get(CommonService);
    const getSet = this.injector.get(GetterSetterService);

    const body = err.error as { statusCode?: string; message?: string } | null | undefined;

    if (shouldSkipSessionExpiryHandling(request, err)) {
      return;
    }

    // Legacy forced-logout code from platform APIs
    if (body && typeof body === 'object' && body.statusCode === 'NS142') {
      getSet.logout();
      try {
        this.injector.get(AuthService).logout();
      } catch {
        /* AuthService may be unavailable during early bootstrap */
      }
      location.reload();
      return;
    }

    // Expired / invalid session — not login/OTP/reset challenge failures
    if (err.status === 401 && !common.isAuthChallengeUrl(request.url)) {
      try {
        const auth = this.injector.get(AuthService);
        const router = this.injector.get(Router);
        auth.logout();
        router.navigate(['/auth/sign-in'], {
          queryParams: {
            returnUrl: router.url.startsWith('/auth') ? '/' : router.url,
            sessionExpired: true,
          },
        });
      } catch {
        getSet.logout();
      }
      common.notifyMessage('Your session has expired. Please sign in again.', 'error');
      return;
    }

    if (request.context.get(SKIP_ERROR_TOAST)) {
      return;
    }

    const ctx = common.getHttpErrorContextFromUrl(request.url);
    common.notifyHttpError(err, ctx);
  }
}
